import { ImonetaryValue, Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { FlyingMailClient } from '../../utils/flying-mail-client';

/**
 * Type representing the subscription status.
 */
type TsubscriptionStatus = 'paid' | 'failed' | 'canceled' | 'pending';

/**
 * Type representing the duration months.
 */
type TdurationMonths = 1 | 2 | 3 | 6 | 12;
interface IFeature {
  name: string;
  quota: number;
  remainingQuota: number;
  usedQuota: number;
  messageLimit: number;
  emailAccountLimit: number;
  remainingEmailAccountLimit: number;
  usedEmailAccountLimit: number;
  emailsPerMonth: number;
  remainingEmailsPerMonth: number;
  usedEmailsPerMonth: number;
  paymentMeta?: Record<string, any>;
}

/**
 * Interface representing a mail subscription.
 */
export interface ImailSubscription {
  id: string;
  company: string;
  defaultDomain: string;
  name: string;
  amount: ImonetaryValue;
  duration: TdurationMonths;
  active: boolean;
  startDate: Date;
  endDate: Date;
  status: TsubscriptionStatus;
  features: IFeature[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Class representing a mail subscription.
 */
export class MailSubscription {
  /**
   * Base URL for mail subscription API endpoints.
   */
  static baseUrl = '/subscription';

  /**
   * The ID of the subscription.
   */
  id: string;

  /**
   * The company associated with the subscription.
   */
  company: string;

  /**
   * The default domain associated with the subscription.
   */
  defaultDomain: string;

  /**
   * The name of the subscription.
   */
  name: string;

  /**
   * The amount of the subscription.
   */
  amount: ImonetaryValue;

  /**
   * The duration of the subscription.
   */
  duration: TdurationMonths;

  /**
   * Whether the subscription is active.
   */
  active: boolean;

  /**
   * The start date of the subscription.
   */
  startDate: Date;

  /**
   * The end date of the subscription.
   */
  endDate: Date;

  /**
   * The status of the subscription.
   */
  status: TsubscriptionStatus;

  /**
   * The features of the subscription.
   */
  features: IFeature[];

  /**
   * The creation date of the subscription.
   */
  createdAt: Date;

  /**
   * The update date of the subscription.
   */
  updatedAt: Date;

  constructor(data: ImailSubscription) {
    this.id = data.id;
    this.company = data.company;
    this.defaultDomain = data.defaultDomain;
    this.name = data.name;
    this.amount = data.amount;
    this.duration = data.duration;
    this.active = data.active;
    this.startDate = data.startDate;
    this.endDate = data.endDate;
    this.status = data.status;
    this.features = data.features;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  /**
   * Retrieves all mail subscriptions.
   *
   * @returns A Promise that resolves to an array of MailSubscription instances.
   */
  static async getAll() {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<ImailSubscription[]>(`${MailSubscription.baseUrl}`);
    const data = await lastValueFrom(observer$);

    return data.map(val => new MailSubscription(val));
  }

  /**
   * Retrieves a specific mail subscription.
   *
   * @param id - The ID of the subscription to retrieve.
   * @returns A Promise that resolves to a new MailSubscription instance.
   */
  static async getOne(id: number) {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<ImailSubscription>(`${MailSubscription.baseUrl}/${id}`);
    const data = await lastValueFrom(observer$);

    return new MailSubscription(data);
  }

  /**
   * Updates a specific mail subscription.
   *
   * @param id - The ID of the subscription to update.
   * @param permissions - The permissions to update.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static update(id: number, permissions: ImailSubscription) {
    const observer$ = FlyingMailClient.ehttp
      .makePut<Isuccess>(`${MailSubscription.baseUrl}/${id}`, permissions);

    return lastValueFrom(observer$);
  }
}
